/**
 * Account recovery route — clears all auth cookies for the current host.
 *
 * Use when a stale session cookie is causing server errors (e.g.,
 * "Failed to decrypt private key" after a JWKS rotation or
 * BETTER_AUTH_SECRET change). This route is intentionally unauthenticated
 * and does not call any session-resolving server function.
 */

import { Button } from '@app/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/auth/recover')({
  component: RecoverPage,
});

function clearJsAccessibleCookies() {
  if (typeof document === 'undefined') return;
  // Best-effort wipe of every JS-accessible cookie on the current host.
  // Better Auth's session cookies are HttpOnly and can't be touched from
  // JS — those are cleared server-side via the signOut Set-Cookie headers.
  // This sweep cleans up non-HttpOnly auxiliaries (analytics, preference
  // cookies, etc.) so the post-recovery state is fully fresh.
  const hostname = window.location.hostname;
  const parentDomain = hostname.split('.').slice(-2).join('.');
  const paths = ['/', '/api', '/api/auth'];
  const domains = [hostname, `.${hostname}`, `.${parentDomain}`];

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim();
    if (!name) continue;
    for (const path of paths) {
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
      }
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    }
  }
}

/**
 * Hit Better Auth's sign-out endpoint so the server emits Set-Cookie
 * headers that clear the HttpOnly session cookies (which JS can't touch).
 * Best-effort — a fully broken session may already 500 on sign-out, but
 * the Set-Cookie headers are still emitted before the error response, so
 * the browser still wipes the cookies. We swallow any error since this
 * page is the user's recovery path.
 */
async function serverSideClearAuth() {
  if (typeof window === 'undefined') return;
  try {
    await fetch('/api/auth/sign-out', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
  } catch {
    // Recovery path — ignore network/auth errors. The cookie sweep below
    // still runs as a backup.
  }
}

function RecoverPage() {
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    (async () => {
      await serverSideClearAuth();
      clearJsAccessibleCookies();
      setCleared(true);
    })();
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-16">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold mb-3">
          {cleared ? 'Session cleared' : 'Clearing session…'}
        </h1>
        <p className="text-muted-foreground mb-6 text-sm">
          {cleared
            ? 'Your local session has been reset. You can sign in again from a clean state.'
            : 'Removing local auth cookies for this site.'}
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button>Sign in</Button>
          </Link>
          <Link to="/">
            <Button variant="outline">Home</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
